import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {RootState, useAppDispatch} from "../../../../redux/store";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import {
    Previsione,
    savePrevisione,
    FASE_COLLEZIONE
} from "./previsioniSlice";
import { ErrorStatusTypes } from "components/common/errorsDeclarations";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { NumericFormat } from "react-number-format";

// ============================================
// INTERFACES
// ============================================

interface PrevisioniInlineFormProps {
    previsione: Previsione;
    onlyField?: 'fase_collezione' | 'qta_bdg';
}

// ============================================
// COMPONENT
// ============================================

const PrevisioniInlineForm = ({ previsione, onlyField }: PrevisioniInlineFormProps) => {
    const dispatch = useAppDispatch();

    // ============================================
    // REACT HOOK FORM SETUP
    // ============================================

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
        watch,
    } = useForm<Previsione>({
        mode: 'onChange',
        defaultValues: {
            ...previsione,
            fase_collezione: previsione.fase_collezione || 'PROT',
            qta_bdg: previsione.qta_bdg || 0,
        },
    });

    // ============================================
    // SELECTORS
    // ============================================

    const previsioniStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.previsioniState.previsioniList.errorsStack.status
    );

    const faseCollezioneCorrente = watch('fase_collezione');
    const nascondiQtaBdg = faseCollezioneCorrente === 'PROT' || faseCollezioneCorrente === 'AN';

    // Reset form quando cambiano i dati
    useEffect(() => {
        reset({
            ...previsione,
            fase_collezione: previsione.fase_collezione || 'PROT',
            qta_bdg: previsione.qta_bdg || 0,
        }, { keepDirty: false });
    }, [previsione.id, previsione.fase_collezione, previsione.qta_bdg, reset]);

    // ============================================
    // SUBMIT HANDLER
    // ============================================

    const onSubmit: SubmitHandler<Previsione> = (data) => {
        dispatch(savePrevisione({
            previsioneToSave: {
                ...previsione,
                fase_collezione: data.fase_collezione,
                qta_bdg: data.qta_bdg,
            }
        }));
    };

    // ============================================
    // RENDER
    // ============================================

    // Se deve mostrare solo lo fase_collezione
    if (onlyField === 'fase_collezione') {
        return (
            <Controller
                name="fase_collezione"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        value={Object.entries(FASE_COLLEZIONE)
                            .map(([key, value]) => ({ key, value }))
                            .find(opt => opt.key === field.value) || undefined}
                        options={Object.entries(FASE_COLLEZIONE)
                            .map(([key, value]) => ({ key, value }))}
                        getOptionLabel={(option) => option?.value || ""}
                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                        onChange={(_, newValue) => {
                            const selectedKey = newValue?.key || "PROT";
                            field.onChange(selectedKey);
                            // Reset qta_bdg se è prototipo
                            // if (selectedKey === 'PROT') {
                            //     setValue('qta_bdg', 0);
                            // }

                            // Submit immediato
                            handleSubmit(onSubmit)();
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                variant="outlined"
                                error={!!errors?.fase_collezione}
                                sx={{
                                    width: '100%',
                                    '& .MuiInput-root': {
                                        fontSize: '0.875rem',
                                    }
                                }}
                            />
                        )}
                        disableClearable
                    />
                )}
            />
        );
    }

    // Se deve mostrare solo la quantità BDG
    if (onlyField === 'qta_bdg') {
        if (nascondiQtaBdg) {
            return  <Box />;
        }
        return (
            <Controller
                name="qta_bdg"
                control={control}
                render={({ field }) => (
                    <NumericFormat
                        customInput={TextField}
                        size="small"
                        variant="outlined"
                        placeholder="Q.tà"
                        disabled={nascondiQtaBdg}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={1}
                        value={field.value}
                        onValueChange={(values) => {
                            field.onChange(values.floatValue || 0);
                        }}
                        onBlur={() => handleSubmit(onSubmit)()}
                        sx={{
                            width: '100%',
                            '& .MuiInput-root': {
                                fontSize: '0.875rem',
                            }
                        }}
                    />
                )}
            />
        );
    }

    // Render originale con entrambi i campi (per retrocompatibilità)
    return (
        <Box
            component="form"
            onSubmit={(e) => e.preventDefault()}
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                minWidth: 0
            }}
        >
            {/* fase_collezione Select */}
            <Controller
                name="fase_collezione"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        value={Object.entries(FASE_COLLEZIONE)
                            .map(([key, value]) => ({ key, value }))
                            .find(opt => opt.key === field.value) || undefined}
                        options={Object.entries(FASE_COLLEZIONE)
                            .map(([key, value]) => ({ key, value }))}
                        getOptionLabel={(option) => option?.value || ""}
                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                        onChange={(_, newValue) => {
                            const selectedKey = newValue?.key || "PROT";
                            field.onChange(selectedKey);
                            // Submit immediato
                            handleSubmit(onSubmit)();
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                variant="outlined"
                                error={!!errors?.fase_collezione}
                                sx={{
                                    minWidth: 120,
                                    '& .MuiInput-root': {
                                        fontSize: '0.875rem',
                                    }
                                }}
                            />
                        )}
                        disableClearable
                    />
                )}
            />

            {/* Quantità BDG */}
            {!nascondiQtaBdg && (
            <Controller
                name="qta_bdg"
                control={control}
                render={({ field }) => (
                    <NumericFormat
                        customInput={TextField}
                        size="small"
                        variant="outlined"
                        placeholder="Q.tà"
                        disabled={nascondiQtaBdg}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={1}
                        value={field.value}
                        onValueChange={(values) => {
                            field.onChange(values.floatValue || 0);
                        }}
                        onBlur={() => handleSubmit(onSubmit)()}
                        sx={{
                            width: 80,
                            '& .MuiInput-root': {
                                fontSize: '0.875rem',
                            }
                        }}
                    />
                )}
            />
                )}
        </Box>
    );
};

export default PrevisioniInlineForm;